import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatRelative } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertOctagon, Plus, X, CheckCircle2, Clock, Loader2, Paperclip, Upload,
  MessageSquare, User as UserIcon, RefreshCw, Search, AlertTriangle,
  ArrowRight, Target, Gauge, Bookmark, Save,
} from "lucide-react";

const SEVERITY_TONE = {
  critical: { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", label: "Critical" },
  high:     { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", label: "High" },
  medium:   { bg: "bg-[#FEF3C7]", text: "text-[#92400E]", label: "Medium" },
  low:      { bg: "bg-[#E0E7FF]", text: "text-[#1E40AF]", label: "Low" },
};

const STATUS_TONE = {
  open:              { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", label: "Open" },
  in_progress:       { bg: "bg-[#DBEAFE]", text: "text-[#1E40AF]", label: "In Progress" },
  awaiting_review:   { bg: "bg-[#FEF3C7]", text: "text-[#92400E]", label: "Awaiting Review" },
  resolved:          { bg: "bg-[#DCFCE7]", text: "text-[#166534]", label: "Resolved" },
  closed:            { bg: "bg-[#F1F5F9]", text: "text-[#334155]", label: "Closed" },
};

const CATEGORIES = [
  "data_governance", "security", "access", "retention",
  "legal_hold", "operational", "custom",
];
const DOMAINS = ["HIPAA", "42_CFR", "SOC2", "NIST", "internal", "other"];
const SEVERITIES = ["critical", "high", "medium", "low"];

// ------- Executive metrics header ------------------------------------
function MetricCard({ label, value, sub, tone = "text-slate-900" }) {
  return (
    <div className="p-4 rounded-2xl border border-[#E5E5E0] bg-white">
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function MetricsHeader({ metrics }) {
  if (!metrics) return null;
  const t = metrics.totals || {};
  return (
    <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="exceptions-metrics">
      <MetricCard label="Open" value={t.open ?? 0} sub={`${t.total ?? 0} total`} tone="text-[#991B1B]" />
      <MetricCard label="Closed" value={t.closed ?? 0} tone="text-[#166534]" />
      <MetricCard label="Overdue" value={t.overdue ?? 0} tone="text-[#92400E]" />
      <MetricCard label="Avg resolution" value={metrics.avg_resolution_hours != null ? `${metrics.avg_resolution_hours}h` : "—"} />
      <MetricCard label="SLA met" value={metrics.sla_met_pct != null ? `${metrics.sla_met_pct}%` : "—"} sub="Of closed exceptions" />
      <MetricCard label="Auto-fix success" value={metrics.auto_fix_success_rate_pct != null ? `${metrics.auto_fix_success_rate_pct}%` : "—"} sub={`Last 90d · ${metrics.auto_fix_runs_90d || 0} runs`} />
    </div>
  );
}

function BreakdownCard({ title, data, Icon }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="p-4 rounded-2xl border border-[#E5E5E0] bg-white">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-[#1E3A8A]" />}
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{title}</div>
      </div>
      {entries.length === 0 ? (
        <div className="text-xs text-slate-400">No data yet.</div>
      ) : (
        <ul className="space-y-1">
          {entries.map(([k, v]) => (
            <li key={k} className="flex items-center justify-between text-sm">
              <span className="text-slate-700 truncate">{k}</span>
              <span className="text-slate-900 font-semibold">{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ------- Create dialog -----------------------------------------------
function CreateExceptionDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "", severity: "medium", category: "operational",
    compliance_domain: "internal", affected_module: "platform",
    recommended_remediation: "", assigned_owner_email: "",
  });
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (form.title.trim().length < 5) {
      toast.error("Title must be at least 5 characters.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.post("/compliance/exceptions", payload);
      toast.success(`Exception ${data.code} created`);
      onCreated?.(data);
      onClose();
      setForm({ ...form, title: "", description: "", recommended_remediation: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="exception-create-dialog">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="w-full max-w-lg bg-white h-full shadow-2xl overflow-y-auto">
        <div className="p-6 border-b border-[#E5E5E0] flex items-start gap-3">
          <AlertOctagon className="w-5 h-5 text-[#1E3A8A] mt-1" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#1E3A8A]">Compliance</div>
            <h2 className="text-lg font-semibold text-slate-900">New Enterprise Exception</h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800"
                  data-testid="exception-create-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <FormField label="Title (≥ 5 chars)">
            <input
              type="text"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg"
              data-testid="exception-create-title"
            />
          </FormField>
          <FormField label="Description">
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg text-sm"
              data-testid="exception-create-description"
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Severity">
              <select value={form.severity} onChange={(e) => set("severity", e.target.value)}
                       className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg bg-white"
                       data-testid="exception-create-severity">
                {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField label="Category">
              <select value={form.category} onChange={(e) => set("category", e.target.value)}
                       className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg bg-white"
                       data-testid="exception-create-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormField>
            <FormField label="Compliance domain">
              <select value={form.compliance_domain} onChange={(e) => set("compliance_domain", e.target.value)}
                       className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg bg-white"
                       data-testid="exception-create-domain">
                {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </FormField>
            <FormField label="Affected module">
              <input value={form.affected_module}
                     onChange={(e) => set("affected_module", e.target.value)}
                     className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg"
                     data-testid="exception-create-module" />
            </FormField>
          </div>
          <FormField label="Assigned owner email (optional)">
            <input value={form.assigned_owner_email}
                   onChange={(e) => set("assigned_owner_email", e.target.value)}
                   placeholder="owner@harborwell.care"
                   className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg"
                   data-testid="exception-create-owner" />
          </FormField>
          <FormField label="Recommended remediation">
            <textarea
              rows={2}
              value={form.recommended_remediation}
              onChange={(e) => set("recommended_remediation", e.target.value)}
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg text-sm"
              data-testid="exception-create-remediation"
            />
          </FormField>

          <div className="pt-4 border-t border-[#E5E5E0] flex gap-2">
            <button type="button" onClick={submit} disabled={saving}
                    className="px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium disabled:opacity-50"
                    data-testid="exception-create-submit">
              {saving ? "Creating…" : "Create exception"}
            </button>
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-lg border border-[#E5E5E0] text-sm">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.14em] text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ------- Detail drawer -----------------------------------------------
function ExceptionDetailDrawer({ exceptionId, onClose, onChange }) {
  const [exc, setExc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [commentBody, setCommentBody] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!exceptionId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/compliance/exceptions/${exceptionId}`);
      setExc(data);
      setOwnerEmail(data.assigned_owner_email || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [exceptionId]);
  useEffect(() => { load(); }, [load]);

  const allowedTransitions = useMemo(() => {
    if (!exc) return [];
    const T = {
      open: ["in_progress", "awaiting_review", "resolved", "closed"],
      in_progress: ["awaiting_review", "resolved", "open"],
      awaiting_review: ["resolved", "in_progress", "open"],
      resolved: ["closed", "in_progress", "open"],
      closed: ["open"],
    };
    return T[exc.status] || [];
  }, [exc]);

  if (!exceptionId) return null;

  const doTransition = async (to_status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/compliance/exceptions/${exceptionId}/transition`, { to_status });
      setExc(data);
      toast.success(`Status → ${to_status}`);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doAssign = async () => {
    if (!ownerEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/compliance/exceptions/${exceptionId}/assign`,
                                       { owner_email: ownerEmail.trim() });
      setExc(data);
      toast.success("Owner reassigned.");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doComment = async () => {
    if (!commentBody.trim()) return;
    setBusy(true);
    try {
      await api.post(`/compliance/exceptions/${exceptionId}/comments`, { body: commentBody.trim() });
      setCommentBody("");
      await load();
      toast.success("Comment added.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("parent_type", "compliance_exception");
      fd.append("parent_id", exceptionId);
      fd.append("file", file);
      await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded · ${file.name}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="exception-detail-drawer">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white h-full shadow-2xl overflow-y-auto">
        <div className="p-6 border-b border-[#E5E5E0] flex items-start gap-3">
          <AlertOctagon className="w-5 h-5 text-[#1E3A8A] mt-1" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#1E3A8A]">
              {exc?.code || "Loading…"}
            </div>
            <h2 className="text-lg font-semibold text-slate-900 break-words">
              {exc?.title || exceptionId}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800"
                  data-testid="exception-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-[#1E3A8A]" />
          </div>
        ) : exc ? (
          <div className="p-6 space-y-6">
            {/* Status + Severity + Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              {(() => {
                const tone = STATUS_TONE[exc.status];
                return (
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${tone.bg} ${tone.text}`}
                        data-testid="exception-detail-status">
                    {tone.label}
                  </span>
                );
              })()}
              {(() => {
                const tone = SEVERITY_TONE[exc.severity] || SEVERITY_TONE.medium;
                return (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.1em] font-semibold ${tone.bg} ${tone.text}`}>
                    {tone.label}
                  </span>
                );
              })()}
              <span className="text-xs text-slate-500">
                Created {formatRelative(exc.created_at)} by {exc.reported_by_email || "—"}
              </span>
            </div>

            {/* Transitions */}
            <div className="flex flex-wrap gap-2">
              {allowedTransitions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => doTransition(s)}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-lg border border-[#1E3A8A] text-[#1E3A8A] text-xs font-medium hover:bg-[#EFF6FF] disabled:opacity-50 flex items-center gap-1.5"
                  data-testid={`exception-transition-${s}`}
                >
                  <ArrowRight className="w-3 h-3" />
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>

            {/* Metadata grid */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Meta label="Category" value={exc.category} />
              <Meta label="Compliance domain" value={exc.compliance_domain} />
              <Meta label="Affected module" value={exc.affected_module} />
              <Meta label="Risk level" value={exc.risk_level} />
              <Meta label="Due date" value={exc.due_date ? new Date(exc.due_date).toLocaleDateString() : "—"} />
              <Meta label="SLA (days)" value={exc.sla_days ?? "—"} />
              <Meta label="Source" value={`${exc.source_kind}${exc.source_ref ? " · " + exc.source_ref.slice(0, 12) : ""}`} />
              <Meta label="Tenant" value={exc.tenant} />
            </div>

            {exc.description && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Description</div>
                <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{exc.description}</div>
              </div>
            )}
            {exc.recommended_remediation && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Remediation</div>
                <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{exc.recommended_remediation}</div>
              </div>
            )}

            {/* Owner assignment */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1">
                Assigned owner
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  placeholder="owner@harborwell.care"
                  className="flex-1 px-3 py-2 border border-[#E5E5E0] rounded-lg text-sm"
                  data-testid="exception-assign-input"
                />
                <button type="button" onClick={doAssign} disabled={busy || !ownerEmail.trim()}
                        className="px-3 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm disabled:opacity-50"
                        data-testid="exception-assign-btn">
                  Assign
                </button>
              </div>
            </div>

            {/* Related items */}
            {((exc.related_compliance_issues || []).length > 0 ||
              (exc.related_legal_holds || []).length > 0 ||
              (exc.related_remediation_actions || []).length > 0) && (
              <div className="p-3 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0]">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">
                  Related items
                </div>
                {(exc.related_compliance_issues || []).length > 0 && (
                  <div className="text-xs text-slate-700">
                    <span className="font-semibold">Compliance issues:</span> {exc.related_compliance_issues.join(", ")}
                  </div>
                )}
                {(exc.related_legal_holds || []).length > 0 && (
                  <div className="text-xs text-slate-700 mt-1">
                    <span className="font-semibold">Legal holds:</span> {exc.related_legal_holds.join(", ")}
                  </div>
                )}
                {(exc.related_remediation_actions || []).length > 0 && (
                  <div className="text-xs text-slate-700 mt-1">
                    <span className="font-semibold">Remediation attempts:</span>{" "}
                    {exc.related_remediation_actions.length} ({exc.related_remediation_actions.filter((r) => r.ok).length} ok)
                  </div>
                )}
              </div>
            )}

            {/* Attachments — shared pipeline */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 flex items-center gap-1.5">
                  <Paperclip className="w-3.5 h-3.5" />
                  Attachments ({exc.attachments_count || 0})
                </div>
                <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border border-[#E5E5E0] hover:bg-slate-50">
                  <Upload className="w-3.5 h-3.5" />
                  {uploading ? "Uploading…" : "Upload"}
                  <input type="file" className="hidden" onChange={doUpload}
                         disabled={uploading}
                         data-testid="exception-attachment-upload" />
                </label>
              </div>
              {(exc.attachments || []).length === 0 ? (
                <p className="text-xs text-slate-500">No evidence attached yet.</p>
              ) : (
                <ul className="border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]"
                    data-testid="exception-attachments-list">
                  {exc.attachments.map((a) => (
                    <li key={a.id} className="p-3 text-sm flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-slate-900">{a.filename}</div>
                        <div className="text-xs text-slate-500">
                          {a.content_type} · {Math.round((a.size || 0) / 1024)} KB · {formatRelative(a.uploaded_at)}
                        </div>
                      </div>
                      <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/attachments/${a.id}/download`}
                         target="_blank" rel="noreferrer"
                         className="text-xs text-[#1E3A8A] underline">Download</a>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Comments */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" />
                Comments ({(exc.comments || []).length})
              </div>
              <ul className="space-y-2 mb-3" data-testid="exception-comments-list">
                {(exc.comments || []).map((c) => (
                  <li key={c.id} className="p-3 rounded-lg bg-[#F9F8F6] border border-[#E5E5E0]">
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      <UserIcon className="w-3 h-3" />
                      {c.author_email || "unknown"} · {formatRelative(c.ts)}
                    </div>
                    <div className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{c.body}</div>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value)}
                  placeholder="Add a comment…"
                  className="flex-1 px-3 py-2 border border-[#E5E5E0] rounded-lg text-sm"
                  data-testid="exception-comment-input"
                />
                <button type="button" onClick={doComment} disabled={busy || !commentBody.trim()}
                        className="px-3 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm disabled:opacity-50"
                        data-testid="exception-comment-submit">
                  Post
                </button>
              </div>
            </div>

            {/* Audit history */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">
                Audit history ({(exc.audit_history || []).length})
              </div>
              <ul className="border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]"
                  data-testid="exception-audit-history">
                {(exc.audit_history || []).map((h, idx) => (
                  <li key={idx} className="p-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-[0.14em] text-[#1E3A8A]">
                        {h.action}
                      </span>
                      <span className="text-xs text-slate-500">
                        {formatRelative(h.ts)} · {h.actor_email || "system"}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-slate-500">Exception not found.</div>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{value == null || value === "" ? "—" : String(value)}</div>
    </div>
  );
}

// ------- Main page ---------------------------------------------------
export default function AdminExceptions() {
  const [exceptions, setExceptions] = useState([]);
  const [counts, setCounts] = useState({});
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [query, setQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [savedFilters, setSavedFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem("harborwell:exceptions:saved_filters") || "[]"); }
    catch { return []; }
  });
  const [savingName, setSavingName] = useState("");
  const [savingOpen, setSavingOpen] = useState(false);

  const persistFilters = (list) => {
    setSavedFilters(list);
    try { localStorage.setItem("harborwell:exceptions:saved_filters", JSON.stringify(list)); } catch { /* ignore */ }
  };
  const saveCurrent = () => {
    if (!savingName.trim()) return;
    const next = [...savedFilters.filter((f) => f.name !== savingName.trim()),
                   { name: savingName.trim(), statusFilter, severityFilter, query }];
    persistFilters(next);
    toast.success(`Filter "${savingName.trim()}" saved.`);
    setSavingName(""); setSavingOpen(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (severityFilter) params.severity = severityFilter;
      const [{ data }, { data: m }] = await Promise.all([
        api.get("/compliance/exceptions", { params }),
        api.get("/compliance/exceptions/metrics"),
      ]);
      setExceptions(data.exceptions || []);
      setCounts(data.counts || {});
      setMetrics(m);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, severityFilter]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return exceptions;
    return exceptions.filter((e) =>
      String(e.code).toLowerCase().includes(q) ||
      String(e.title).toLowerCase().includes(q) ||
      String(e.affected_module).toLowerCase().includes(q) ||
      String(e.assigned_owner_email || "").toLowerCase().includes(q)
    );
  }, [exceptions, query]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-16">
      <div className="max-w-6xl mx-auto px-6 pt-8">
        {/* Header */}
        <div className="p-6 rounded-3xl bg-white border border-[#E5E5E0] shadow-sm">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.18em] text-[#1E3A8A]">
                Enterprise Governance · Exception Management
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 mt-1">Compliance Exceptions</h1>
              <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                Platform-wide exception registry — any HarborWell module can create, assign, track,
                and audit compliance exceptions through this shared engine. Attachments reuse the
                central document management pipeline.
              </p>
            </div>
            <button type="button" onClick={() => setCreateOpen(true)}
                    className="px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium flex items-center gap-2"
                    data-testid="exception-new">
              <Plus className="w-4 h-4" />
              New exception
            </button>
            <button type="button" onClick={load}
                    className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm bg-white flex items-center gap-2"
                    data-testid="exception-refresh">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Executive metrics */}
        <div className="mt-6">
          <MetricsHeader metrics={metrics} />
        </div>

        {/* Breakdown cards */}
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <BreakdownCard title="By severity" data={metrics?.by_severity} Icon={AlertTriangle} />
          <BreakdownCard title="By module" data={metrics?.by_module} Icon={Target} />
          <BreakdownCard title="By owner (open)" data={metrics?.by_owner} Icon={UserIcon} />
        </div>

        {/* Status filter pills */}
        <div className="mt-6 flex gap-2 flex-wrap">
          {[
            { key: "", label: "All", count: (counts.open || 0) + (counts.in_progress || 0) + (counts.awaiting_review || 0) + (counts.resolved || 0) + (counts.closed || 0) },
            { key: "open", label: "Open", count: counts.open || 0 },
            { key: "in_progress", label: "In Progress", count: counts.in_progress || 0 },
            { key: "awaiting_review", label: "Awaiting Review", count: counts.awaiting_review || 0 },
            { key: "resolved", label: "Resolved", count: counts.resolved || 0 },
            { key: "closed", label: "Closed", count: counts.closed || 0 },
          ].map((p) => (
            <button
              key={p.key || "all"}
              type="button"
              onClick={() => setStatusFilter(p.key)}
              className={`px-3 py-1.5 rounded-full text-xs border ${
                statusFilter === p.key
                  ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                  : "bg-white text-slate-700 border-[#E5E5E0] hover:border-[#94A3B8]"
              }`}
              data-testid={`exception-status-filter-${p.key || "all"}`}
            >
              {p.label} <span className="ml-1 opacity-80">{p.count}</span>
            </button>
          ))}
          <div className="mx-2 h-6 w-px bg-[#E5E5E0]" />
          {["", "critical", "high", "medium", "low"].map((s) => (
            <button
              key={s || "sev_all"}
              type="button"
              onClick={() => setSeverityFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs border ${
                severityFilter === s
                  ? "bg-[#991B1B] text-white border-[#991B1B]"
                  : "bg-white text-slate-700 border-[#E5E5E0] hover:border-[#94A3B8]"
              }`}
              data-testid={`exception-severity-filter-${s || "all"}`}
            >
              {s || "All severities"}
            </button>
          ))}
        </div>

        {/* Search + list */}
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by code, title, module, or owner…"
              className="flex-1 px-3 py-2 border border-[#E5E5E0] rounded-lg bg-white text-sm"
              data-testid="exception-search"
            />
          </div>

          {/* Saved filters bar */}
          <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="exceptions-saved-filters-bar">
            <Bookmark className="w-3.5 h-3.5 text-[#1E3A8A]" />
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Saved filters</div>
            {savedFilters.length === 0 && <span className="text-xs text-slate-400">none yet</span>}
            {savedFilters.map((f) => (
              <button
                key={f.name}
                type="button"
                onClick={() => {
                  setStatusFilter(f.statusFilter || "");
                  setSeverityFilter(f.severityFilter || "");
                  setQuery(f.query || "");
                }}
                className="group px-3 py-1 rounded-full border border-[#E5E5E0] text-xs bg-white hover:border-[#94A3B8] flex items-center gap-1.5"
                data-testid={`exceptions-saved-filter-${f.name.toLowerCase().replace(/\W+/g, "-")}`}
              >
                {f.name}
                <span
                  className="opacity-0 group-hover:opacity-100 text-[#991B1B]"
                  onClick={(e) => {
                    e.stopPropagation();
                    persistFilters(savedFilters.filter((x) => x.name !== f.name));
                  }}
                  role="button"
                >×</span>
              </button>
            ))}
            {savingOpen ? (
              <div className="inline-flex items-center gap-1.5">
                <input
                  type="text"
                  value={savingName}
                  onChange={(e) => setSavingName(e.target.value)}
                  placeholder="Filter name"
                  className="px-2 py-1 border border-[#E5E5E0] rounded-lg text-xs"
                  data-testid="exceptions-saved-filter-name"
                  autoFocus
                />
                <button type="button" onClick={saveCurrent}
                        className="px-2 py-1 rounded-lg bg-[#1E3A8A] text-white text-xs"
                        data-testid="exceptions-saved-filter-save">Save</button>
                <button type="button" onClick={() => setSavingOpen(false)}
                        className="text-xs text-slate-500">Cancel</button>
              </div>
            ) : (
              <button type="button" onClick={() => setSavingOpen(true)}
                      className="text-xs text-[#1E3A8A] underline"
                      data-testid="exceptions-saved-filter-new">
                <Save className="w-3 h-3 inline mr-1" />
                Save current
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-12 flex items-center justify-center border border-[#E5E5E0] rounded-2xl bg-white">
              <Loader2 className="w-5 h-5 animate-spin text-[#1E3A8A]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 border border-[#E5E5E0] rounded-2xl bg-white text-center text-sm text-slate-500">
              No exceptions match the current filter.
            </div>
          ) : (
            <ul className="border border-[#E5E5E0] rounded-2xl divide-y divide-[#E5E5E0] bg-white"
                data-testid="exception-list">
              {filtered.map((e) => {
                const sTone = STATUS_TONE[e.status] || STATUS_TONE.open;
                const vTone = SEVERITY_TONE[e.severity] || SEVERITY_TONE.medium;
                const overdue = e.due_date && new Date(e.due_date) < new Date() &&
                                !["resolved", "closed"].includes(e.status);
                return (
                  <li key={e.id}
                      onClick={() => setActiveId(e.id)}
                      className="p-4 flex items-center gap-4 hover:bg-slate-50 cursor-pointer"
                      data-testid={`exception-row-${e.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#1E3A8A]">
                          {e.code}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.1em] font-semibold ${sTone.bg} ${sTone.text}`}>
                          {sTone.label}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.1em] font-semibold ${vTone.bg} ${vTone.text}`}>
                          {vTone.label}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.12em] text-slate-500">
                          {e.category}
                        </span>
                        {overdue && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.1em] font-semibold bg-[#FEF3C7] text-[#92400E]">
                            Overdue
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm font-medium text-slate-900 truncate">{e.title}</div>
                      <div className="mt-0.5 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                        <span>{e.affected_module}</span>
                        <span>·</span>
                        <span>{e.assigned_owner_email || "unassigned"}</span>
                        {e.due_date && <>
                          <span>·</span>
                          <span><Clock className="w-3 h-3 inline mr-1" />Due {new Date(e.due_date).toLocaleDateString()}</span>
                        </>}
                      </div>
                    </div>
                    <div className="text-xs text-[#1E3A8A]">Open →</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <CreateExceptionDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
      <ExceptionDetailDrawer exceptionId={activeId} onClose={() => setActiveId(null)} onChange={load} />
    </div>
  );
}
